import Button from '@/common/components/base/button/Button';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import { ImageType } from '@/common/components/imageGallery/components/Extensions/moreItemsExtension/moreItemsExtension.type';
import ImageCard from '@/common/components/imageGallery/components/ImageCard';
import FixToBottom from '@/common/components/layouts/FixToBottom';
import MainLayout from '@/common/components/layouts/MainLayout';
import AttachedUploadImageCard from '@/common/components/uploadImage/attachedUploadImageCard/AttachedUploadImageCard';
import LoadingPage from '@/common/pages/LoadingPage';
import OtherDamagedPreviewImages from '@/modules/adjusterReportModules/IncidentParticipants/components/otherDamagedPreviewImages/OtherDamagedPreviewImages';
import { deleteAdjusterOtherDamagedImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/deleteAdjusterOtherDamagedImages/deleteAdjusterOtherDamagedImagesQueryOptions';
import { getAdjusterOtherDamagedImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getAdjusterOtherDamagedImages/getAdjusterOtherDamagedImagesQueryOptions';
import { postAdjusterOtherDamagedImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/postAdjusterOtherDamagedImages/postDamagedAdjusterDocumentImagesQueryOptions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';

const OtherDamagedImages = () => {
  const queryClient = useQueryClient();
  const { id, damagedId } = useParams();
  const [images, setImages] = useState<Array<ImageType & { id: string }>>([]);
  const [showCamera, setShowCamera] = useState(false);
  const { mutate } = useMutation(postAdjusterOtherDamagedImagesQueryOptions());
  const { data: imagesData, isPending } = useQuery(
    getAdjusterOtherDamagedImagesQueryOptions({
      damaged_id: damagedId!,
      id: id!,
    }),
  );
  const { mutate: deleteImage, isPending: deleteImageLoading } = useMutation(
    deleteAdjusterOtherDamagedImagesQueryOptions(),
  );

  const invalidateImagesList = () => {
    queryClient.invalidateQueries({
      queryKey: getAdjusterOtherDamagedImagesQueryOptions({
        damaged_id: damagedId!,
        id: id!,
      }).queryKey,
    });
  };
  const updateImages = ({ imageId }: { imageId: string }) => {
    const newImages = images.filter((item) => {
      return item.id !== imageId;
    });
    if (!newImages.length) {
      setShowCamera(false);
    }
    setImages(newImages);
  };
  const onEditImage = ({ image }: { image: ImageType & { id: string } }) => {
    setImages((prev) => {
      return prev.map((item) => (item.id === image.id ? image : item));
    });
  };

  const uploadImages = () => {
    mutate(
      {
        damagedId: damagedId!,
        id: id!,
        data: images.map((item) => ({ image: item.blob })),
      },
      {
        onSuccess() {
          setImages([]);
          setShowCamera(false);
          invalidateImagesList();
        },
      },
    );
  };

  // const uploadSingleImage = ({
  //   onSuccess,
  //   data,
  // }: {
  //   onSuccess: () => void;
  //   data: ImageType;
  // }) => {
  //   mutate(
  //     {
  //       damagedId: damagedId!,
  //       id: id!,
  //       data: [
  //         {
  //           image: data.blob,
  //         },
  //       ],
  //     },
  //     {
  //       onSuccess() {
  //         onSuccess();
  //         queryClient.invalidateQueries({
  //           queryKey: getAdjusterOtherDamagedImagesQueryOptions({
  //             damaged_id: damagedId!,
  //             id: id!,
  //           }).queryKey,
  //         });
  //       },
  //     },
  //   );
  // };

  if (isPending) {
    return <LoadingPage />;
  }
  return (
    <>
      {showCamera ? (
        <>
          <OtherDamagedPreviewImages
            images={images}
            updateImages={updateImages}
            editImage={onEditImage}
          />
          <FixToBottom className={css({ bottom: 20 })}>
            <Button fullWidth onClick={uploadImages} marginTop="6px">
              ثبت
            </Button>
          </FixToBottom>
        </>
      ) : (
        <MainLayout
          title="زیان‌دیده / تصاویر"
          header="titleHeader"
          hasBottomPadding
        >
          <Box pt={'1.5rem'}>
            <TitleDescription>تصاویر زیان‌دیده</TitleDescription>
            <Typography color="black-1" my="1.5rem">
              کارشناس محترم، در صورت نیاز می‌توانید تصاویری از زیان‌دیده را ثبت
              نمایید.
            </Typography>
            <Flex
              gap="1rem"
              maxWidth="maxContainer"
              margin="auto"
              mt="1.5rem"
              className={grid({ columns: 2 })}
            >
              {imagesData?.length! >= 15 ? null : (
                <AttachedUploadImageCard
                  onUploadImage={(data) => {
                    const imageData = data.imageData.map((item) => {
                      const id = crypto.randomUUID();
                      return { ...item, id };
                    });
                    setShowCamera(true);
                    setImages((prev) => [...prev, ...imageData]);
                    data.closeModal();
                  }}
                  title="افزودن تصویر"
                  uploadImageLoading={false}
                  totalImages={15 - imagesData?.length!}
                />
              )}
              {imagesData?.map((image) => {
                return (
                  <ImageCard
                    key={image.id}
                    imageData={{
                      name: image.title?.fa_const,
                      src: image.image,
                    }}
                    extension={{
                      type: 'moreItems',
                      items: ['deletePhoto', 'downloadPhoto'],
                      handleClick: {
                        deletePhotoOnClick: ({ closeModal }) => {
                          deleteImage(
                            {
                              damagedId: damagedId!,
                              id: id!,
                              image_id: image.id,
                            },
                            {
                              onSuccess: () => {
                                closeModal();
                                invalidateImagesList();
                              },
                            },
                          );
                        },
                      },
                      deleteImageLoading,
                    }}
                  />
                );
              })}
            </Flex>
          </Box>
        </MainLayout>
      )}
    </>
  );
};

export default OtherDamagedImages;
